import { Link, NavLink, useNavigate } from "react-router-dom";
import { Gamepad2, MessageSquare, ShieldCheck, LogOut, Users, Wrench, Lightbulb, PartyPopper } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isOwnerPanelUser } from "@/lib/ownerAccess";

export default function NavBar({ rightSlot = null }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const linkClass = ({ isActive }) =>
    `px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
      isActive
        ? "bg-[#ff6a00] text-white"
        : "text-[#5c534d] hover:text-[#1a1613] hover:bg-[#ffe4d1]"
    }`;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-30 bg-[#fff7f0]/85 backdrop-blur-md border-b border-[#efe3d6]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between gap-4">
        <Link
          to="/"
          className="flex items-center gap-2.5 group shrink-0"
          data-testid="brand-link"
        >
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#ff6a00] text-white shadow-[0_6px_18px_-6px_rgba(255,106,0,0.6)] group-hover:rotate-[-6deg] transition-transform">
            <Gamepad2 size={20} strokeWidth={2.4} />
          </span>
          <span className="font-display font-bold text-base sm:text-xl tracking-tight hidden sm:block">
            Learning <span className="text-[#ff6a00]">Games</span>
          </span>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2 flex-wrap">
          <NavLink to="/" end className={linkClass} data-testid="nav-games">
            <span className="inline-flex items-center gap-1.5">
              <Gamepad2 size={14} /> Games
            </span>
          </NavLink>
          <NavLink to="/community" className={linkClass} data-testid="nav-community">
            <span className="inline-flex items-center gap-1.5">
              <Users size={14} /> Community
            </span>
          </NavLink>
          <NavLink to="/gamemaker" className={linkClass} data-testid="nav-gamemaker">
            <span className="inline-flex items-center gap-1.5">
              <Wrench size={14} /> Game Maker
            </span>
          </NavLink>
          <NavLink to="/party" className={linkClass} data-testid="nav-party">
            <span className="inline-flex items-center gap-1.5">
              <PartyPopper size={14} /> Party Games
            </span>
          </NavLink>
          <NavLink to="/chat" className={linkClass} data-testid="nav-chat">
            <span className="inline-flex items-center gap-1.5">
              <MessageSquare size={14} /> Chat
            </span>
          </NavLink>
          <NavLink to="/suggest" className={linkClass} data-testid="nav-suggest">
            <span className="inline-flex items-center gap-1.5">
              <Lightbulb size={14} /> Suggest
            </span>
          </NavLink>
          {isOwnerPanelUser(user) && (
            <NavLink to="/owner" className={linkClass} data-testid="nav-owner">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck size={14} /> Owner
              </span>
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {rightSlot}
          {user && (
            <div className="flex items-center gap-2">
              <span
                className="hidden md:inline text-sm text-[#5c534d]"
                data-testid="nav-username"
              >
                @{user.username}
                {user.role === "owner" && (
                  <span className="ml-1 text-[10px] font-bold uppercase tracking-widest text-[#ff6a00]">
                    owner
                  </span>
                )}
              </span>
              <button
                onClick={() => {
                  logout();
                  nav("/login");
                }}
                data-testid="logout-btn"
                className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full border border-[#efe3d6] hover:border-[#ff6a00] hover:text-[#ff6a00] transition-colors"
              >
                <LogOut size={13} /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
